// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { createPromiseAction, resolvePromiseAction } from '@adobe/redux-saga-promise';
import * as Sentry from '@sentry/browser';
import { Action, AnyAction, Dispatch, Reducer } from 'redux';
import { PutEffect } from 'redux-saga/effects';
import { call, takeEvery } from 'typed-redux-saga';

import { ErrorWithCode, UnknownError } from '@/utils/errors';

export interface PromiseActionResult<T, E extends ErrorWithCode> {
  data?: T;
  error?: E;
}

export interface ActionWithPayload<P> extends Action<string> {
  type: string;
  payload: P;
}

export type ActionWithoutPayload = Omit<ActionWithPayload<any>, 'payload'>;

export type DispatchActionWithError<R> = (
  func: Dispatch<any> | ((action: ActionWithoutPayload) => PutEffect<ActionWithoutPayload>),
) => Promise<PromiseActionResult<R, ErrorWithCode>>;

export interface AsyncActionWithPayload<P, R> extends DispatchActionWithError<R> {
  pure: ActionWithPayload<P>;
}

export interface AsyncActionFactoryWithPayload<P, R> {
  type: string;
  promiseAction: (payload: P) => ActionWithPayload<P>;

  (payload: P): AsyncActionWithPayload<P, R>;
}

export type AsyncAction<R> = AsyncActionWithPayload<void, R>;

export type AsyncActionFactory<R> = AsyncActionFactoryWithPayload<void, R>;

export interface SyncAction extends SyncActionWithPayload<any> {
  pure: ActionWithPayload<any>;

  (dispatch: Dispatch<any>): void;
}

export interface SyncActionFactory extends SyncActionFactoryWithPayload<any> {
  type: string;

  (): SyncAction;
}

export interface SyncActionWithPayload<P> {
  pure: ActionWithPayload<P>;

  (dispatch: Dispatch<any>): void;
}

export interface SyncActionFactoryWithPayload<P> {
  type: string;

  (payload: P): SyncActionWithPayload<P>;
}

export interface ActionCreator {
  asyncActionWithPayload<P, R>(type: string): AsyncActionFactoryWithPayload<P, R>;

  asyncAction<R>(type: string): AsyncActionFactory<R>;

  syncActionWithPayload<P>(type: string): SyncActionFactoryWithPayload<P>;

  syncAction(type: string): SyncActionFactory;
}

export function actionCreator(namespace: string): ActionCreator {
  return {
    asyncActionWithPayload<P, R>(type: string): AsyncActionFactoryWithPayload<P, R> {
      const promiseAction = createPromiseAction(`${namespace}/${type}`);
      const f = (payload: P): AsyncActionWithPayload<P, R> => {
        const pure = promiseAction(payload);
        const d = (
          func: Dispatch<any> | ((action: ActionWithoutPayload) => PutEffect<ActionWithoutPayload>),
        ): Promise<PromiseActionResult<R, ErrorWithCode>> => func(pure);
        d.pure = pure;
        return d;
      };
      f.type = `${namespace}/${type}`;
      f.promiseAction = promiseAction;
      return f;
    },
    asyncAction<R>(type: string): AsyncActionFactory<R> {
      const promiseAction = createPromiseAction(`${namespace}/${type}`);
      const f = (): AsyncAction<R> => {
        const pure = promiseAction({});
        const d = (
          func: Dispatch<any> | ((action: ActionWithoutPayload) => PutEffect<ActionWithoutPayload>),
        ): Promise<PromiseActionResult<R, ErrorWithCode>> => func(pure);
        d.pure = pure;
        return d;
      };
      f.type = `${namespace}/${type}`;
      f.promiseAction = promiseAction;
      return f;
    },
    syncActionWithPayload<P>(type: string): SyncActionFactoryWithPayload<P> {
      const f = (payload: P): SyncActionWithPayload<P> => {
        const pure = {
          type: `${namespace}/${type}`,
          payload,
        };
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const d = (dispatch: Dispatch<any>): void => dispatch(pure);
        d.pure = pure;
        return d;
      };
      f.type = `${namespace}/${type}`;
      return f;
    },
    syncAction(type: string): SyncActionFactory {
      const f = (): SyncAction => {
        const pure = {
          type: `${namespace}/${type}`,
          payload: '',
        };
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const d = (dispatch: Dispatch<any>): void => dispatch(pure);
        d.pure = pure;
        return d;
      };
      f.type = `${namespace}/${type}`;
      return f;
    },
  };
}

export const withErrorHandler = <T extends Action>(
  worker: (action: T) => unknown,
  onError?: (error: any) => void,
): ((action: T) => Generator) =>
  function* runner(action: T): Generator<unknown, void, T> {
    try {
      yield worker(action);
    } catch (e) {
      console.error(e);
      Sentry.captureException(e instanceof Error ? e : new Error());
      yield onError?.(e);
    }
  };

export const withPromiseHandler = <P, T = ActionWithPayload<P>>(
  worker: (action: T) => Generator,
  onError?: (error: any) => Generator,
): ((action: T) => Generator<unknown, any, T>) =>
  function* runner(action: T): Generator<unknown, any, T> {
    try {
      const result = yield* worker(action);
      yield* call(resolvePromiseAction, action, { data: result });
    } catch (e) {
      console.error(e);
      Sentry.captureException(e instanceof Error ? e : new Error());
      const handled = onError ? yield* onError(e) : undefined;
      yield* call(resolvePromiseAction, action, {
        // eslint-disable-next-line no-nested-ternary
        error: handled instanceof ErrorWithCode ? handled : e instanceof ErrorWithCode ? e : new UnknownError(e),
      });
    }
  };

export const takeEveryTypesafe = <P, T = ActionWithPayload<P>>(
  factory: AsyncActionFactoryWithPayload<P, any>,
  worker: (action: T) => Generator,
  onError?: (error: any) => Generator,
): Generator => takeEvery(factory.promiseAction as any, withPromiseHandler(worker, onError));

export interface ActionType<T, P> extends AnyAction {
  type: T;
  payload: P;
}

export const createReducer = <S>(
  initialState: S,
  handlers: { [k in string]: (state: S, action: ActionType<string, unknown>) => S },
): Reducer<S> =>
  ((state: S, action: ActionType<string, unknown>): S =>
    handlers[action.type]?.(state, action) || state || initialState) as Reducer<S>;

// FIXME: link SyncActionFactoryWithPayload generic to payload type!!!
export const createReducerTypeSafe = <S>(
  initialState: S,
  handlers: [SyncActionFactoryWithPayload<any>, (state: S, payload: any) => S][],
): Reducer<S> =>
  createReducer(
    initialState,
    Object.fromEntries(
      handlers.map((value) => [
        value[0].type,
        (state: S, action: ActionType<string, unknown>): S => value[1](state, action.payload),
      ]),
    ),
  );
